import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Autocomplete, Button, Checkbox, Chip, FormControl, InputLabel, MenuItem, Select, TextField, createFilterOptions } from '@mui/material';
import Dayjs from 'dayjs';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FilterBar, useFilterBarStyles } from '../ui';
import { MIN_DATE_SENTINEL, dateParam } from '../../lib/orderFilters';
import { mockups_tags } from '../../mockups_tags';
import { asFlag } from '../../lib/mockupEvents';
import ProductUrlFilterPopup, { AppliedFilters } from './ProductUrlFilterPopup';
import { useStyles } from './styles';

type TagOption = { tagId: number; tagName: string };

const STATUS_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'In cart', value: 'inCart' },
    { label: 'Ordered', value: 'inOrder' },
];

const TEMPLATE_OPTIONS = [
    { label: 'All', value: 'all' },
    { label: 'Only A/B test', value: 'onlyTest' },
];

const TAGS_DEBOUNCE_DELAY = 300;
export const PREVIEW_PAGE_SIZE = 500;

const createRequestId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const today = () => Dayjs(new Date()).format('YYYY-MM-DD');

const arraysAreEqual = (a: number[] = [], b: number[] = []) => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false;
    }
    return true;
};

const filterTagOptions = createFilterOptions<TagOption>({ limit: 60, stringify: (option) => option.tagName });
const tagById = new Map<number, TagOption>((mockups_tags as TagOption[]).map((tag) => [tag.tagId, tag]));

export interface MockupHistoryFiltersProps {
    queryParams: any;
    loadMockups: (params: any) => void;
    loading?: boolean;
    /** Coloring / Blanket pin a generation type; it is kept on reset. */
    defaultTypeID?: number;
}

/**
 * The filter strip of the preview grids (status, A/B template, tags, dates, the "More filters" dialog).
 * Every change sends a new request; the view controls (columns, tags, grouping) live in `MockupViewControls`.
 */
export default function MockupHistoryFilters({ queryParams, loadMockups, loading = false, defaultTypeID }: MockupHistoryFiltersProps) {
    const classes = useFilterBarStyles();
    const own = useStyles();
    const [localSelectedTags, setLocalSelectedTags] = useState<number[]>(queryParams?.tags || []);
    const tagsDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingTagsRef = useRef<number[]>(queryParams?.tags || []);
    const localSelectedTagsRef = useRef<number[]>(queryParams?.tags || []);

    useEffect(() => {
        localSelectedTagsRef.current = localSelectedTags;
    }, [localSelectedTags]);

    // Keep the optimistic tag selection in step with the store (e.g. after a reset).
    useEffect(() => {
        const incomingTags = queryParams?.tags || [];
        if (!arraysAreEqual(incomingTags, localSelectedTagsRef.current)) {
            setLocalSelectedTags(incomingTags);
            pendingTagsRef.current = incomingTags;
            localSelectedTagsRef.current = incomingTags;
        }
    }, [queryParams?.tags]);

    useEffect(() => {
        return () => {
            if (tagsDebounceTimerRef.current) clearTimeout(tagsDebounceTimerRef.current);
        };
    }, []);

    const dispatchFiltersRequest = (params: any) => {
        loadMockups({ ...params, requestId: createRequestId() });
    };

    const handleFilterChange = (e: { target: { name: string; value: any } }) => {
        const { name, value } = e.target;
        const newQueryParams: any = {
            ...queryParams,
            page: 1,
            lastEvaluatedKey: null,
            [name]: value,
        };
        if (defaultTypeID) newQueryParams.defaultTypeID = defaultTypeID;

        if (name === 'status') {
            delete newQueryParams.status;
            if (value === 'inCart') {
                delete newQueryParams.orderExist;
                newQueryParams.toCart = 1;
            }
            if (value === 'inOrder') {
                delete newQueryParams.toCart;
                newQueryParams.orderExist = 1;
            }
            if (value === 'all') {
                delete newQueryParams.toCart;
                delete newQueryParams.orderExist;
            }
        }
        if (name === 'template') {
            if (value === 'onlyTest') newQueryParams.template = 'ab-test';
            if (value === 'all') delete newQueryParams.template;
        }
        if (name === 'maxDate') {
            if (!newQueryParams.minDate) {
                newQueryParams.minDate = value;
            } else if (dayjs(newQueryParams.maxDate).isBefore(dayjs(newQueryParams.minDate))) {
                newQueryParams.minDate = newQueryParams.maxDate;
            }
        }
        if (name === 'minDate' && !newQueryParams.maxDate) {
            newQueryParams.maxDate = today();
        }
        if (name === 'tags' && (!value || (Array.isArray(value) && value.length === 0))) {
            delete newQueryParams.tags;
        }
        dispatchFiltersRequest(newQueryParams);
    };

    const handleTagsChange = (tags: number[]) => {
        setLocalSelectedTags(tags);
        pendingTagsRef.current = tags;
        if (tagsDebounceTimerRef.current) clearTimeout(tagsDebounceTimerRef.current);
        tagsDebounceTimerRef.current = setTimeout(() => {
            handleFilterChange({ target: { name: 'tags', value: pendingTagsRef.current } });
        }, TAGS_DEBOUNCE_DELAY);
    };

    const onDate = (name: 'minDate' | 'maxDate') => (value: Dayjs.Dayjs | null) => {
        const iso = dateParam(value);
        if (iso) handleFilterChange({ target: { name, value: iso } });
    };

    const reset = () => {
        if (tagsDebounceTimerRef.current) clearTimeout(tagsDebounceTimerRef.current);
        dispatchFiltersRequest({
            ...(defaultTypeID && { defaultTypeID }),
            limit: PREVIEW_PAGE_SIZE,
            maxDate: today(),
            minDate: today(),
        });
    };

    const handleProductUrlFilter = (filters: AppliedFilters) => {
        const newQueryParams: any = { ...queryParams, page: 1, lastEvaluatedKey: null };
        delete newQueryParams.productIds;
        delete newQueryParams.urlPathIds;
        delete newQueryParams.eventId;
        delete newQueryParams.tempEventId;
        delete newQueryParams.customerId;
        delete newQueryParams.template;
        if (filters.productIds && filters.productIds.length > 0) newQueryParams.productIds = filters.productIds.join(',');
        if (filters.urlPathIds && filters.urlPathIds.length > 0) newQueryParams.urlPathIds = filters.urlPathIds.join(',');
        if (filters.eventId) newQueryParams.eventId = filters.eventId;
        if (filters.tempEventId) newQueryParams.tempEventId = filters.tempEventId;
        if (filters.customerId) newQueryParams.customerId = filters.customerId;
        if (filters.template) newQueryParams.template = filters.template;
        dispatchFiltersRequest(newQueryParams);
    };

    const statusValue = asFlag(queryParams?.toCart) ? 'inCart' : asFlag(queryParams?.orderExist) ? 'inOrder' : 'all';
    const templateValue = !queryParams?.template ? 'all' : queryParams.template === 'ab-test' ? 'onlyTest' : 'custom';
    const selectedTagOptions: TagOption[] = localSelectedTags.map((id) => tagById.get(id) || { tagId: id, tagName: String(id) });

    return (
        <FilterBar label="Mockup filters">
            <FormControl size="small" className={classes.select}>
                <InputLabel id="mockup-status-label">Status</InputLabel>
                <Select labelId="mockup-status-label" label="Status" name="status" value={statusValue} onChange={handleFilterChange} disabled={loading}>
                    {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl size="small" className={classes.select}>
                <InputLabel id="mockup-template-label">Mockups</InputLabel>
                <Select labelId="mockup-template-label" label="Mockups" name="template" value={templateValue} onChange={handleFilterChange} disabled={loading}>
                    {TEMPLATE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                    {templateValue === 'custom' && (
                        <MenuItem value="custom" disabled>Template: {String(queryParams.template)}</MenuItem>
                    )}
                </Select>
            </FormControl>
            <Autocomplete
                multiple
                size="small"
                className={own.tags}
                options={mockups_tags as TagOption[]}
                value={selectedTagOptions}
                isOptionEqualToValue={(option, value) => option.tagId === value.tagId}
                getOptionLabel={(option) => option.tagName}
                filterOptions={filterTagOptions}
                disableCloseOnSelect
                limitTags={1}
                onChange={(_, value) => handleTagsChange(value.map((option) => option.tagId))}
                renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.tagId}>
                        <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0 }} tabIndex={-1} />
                        {option.tagName}
                    </li>
                )}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => <Chip size="small" label={option.tagName} {...getTagProps({ index })} key={option.tagId} />)
                }
                renderInput={(params) => <TextField {...params} label="Tags" placeholder={localSelectedTags.length ? undefined : 'Search tags'} />}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    className={classes.date}
                    label="From"
                    format="YYYY-MM-DD"
                    value={queryParams?.minDate && queryParams?.minDate !== MIN_DATE_SENTINEL ? Dayjs(queryParams.minDate) : null}
                    maxDate={queryParams?.maxDate ? Dayjs(queryParams.maxDate) : Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDate('minDate')}
                    slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                    className={classes.date}
                    label="To"
                    format="YYYY-MM-DD"
                    value={queryParams?.maxDate ? Dayjs(queryParams.maxDate) : Dayjs(new Date())}
                    maxDate={Dayjs(new Date())}
                    disabled={loading}
                    onChange={onDate('maxDate')}
                    slotProps={{ textField: { size: 'small' } }}
                />
            </LocalizationProvider>
            <ProductUrlFilterPopup queryParams={queryParams} onApplyFilter={handleProductUrlFilter} loading={loading} />
            <Button className={classes.clear} variant="text" size="small" onClick={reset} disabled={loading}>
                Reset
            </Button>
        </FilterBar>
    );
}
